<template>
    <div class="d-flex gap-4 flex-column">

        <Card :header="$t('currentAvailability')" :tooltip="$t('tooltip')" overlay :loading="loading">

            <template #nav>
                <div :key="spaceId" class="d-flex gap-3 flex-column">
                    <div class="d-flex gap-3">
                        <Multiselect
                            :value="availabilities"
                            :fetch-options-function="fetchOfferAvailabilities"
                            :placeholder="$t('availabilityTime')"
                            :selected-options-label="$t('selectedAvailabilities')"
                            searchable
                            track-by="value"
                            label="label"
                            select-label=""
                            :disabled="loading"
                            show-checkboxes
                            show-selected
                            multiple
                            preselect-all
                            :close-on-select="false"
                            :clear-on-select="false"
                            :allow-empty="false"
                            class="width-200"
                            @changeTrackBy="availabilities = $event"
                            @loading="offerAvailabilitiesLoading = $event"
                        />
                        <MultiselectAvailableCampaigns :allow-empty="false" :value="campaigns" :disabled="loading" @changeTrackBy="campaigns = $event" @loading="campaignsLoading = $event" />
                        <Multiselect
                            v-show="showProductGroupsResults"
                            :value="groups"
                            :fetch-options-function="fetchSpaceGroups"
                            :placeholder="$t('productGroup')"
                            :selected-options-label="$t('selectedGroups')"
                            searchable
                            track-by="id"
                            label="name"
                            select-label=""
                            :disabled="loading"
                            show-checkboxes
                            show-selected
                            multiple
                            preselect-all
                            :close-on-select="false"
                            :clear-on-select="false"
                            class="width-200"
                            @changeTrackBy="groups = $event"
                            @loading="spaceGroupsLoading = $event"
                        />
                        <FormCategoryTreeSelect
                            v-show="!showProductGroupsResults"
                            ref="FormCategoryTreeSelect"
                            :value="categories"
                            :disabled="loading"
                            class="width-200"
                            preselect
                            prevent-empty-selection
                            :clearable="false"
                            @change="categories = $event"
                            @loading="categoriesLoading = $event"
                        />
                    </div>
                    <div class="d-flex gap-3 justify-content-end align-items-center">
                        <b-form-checkbox v-model="showProductGroupsResults" switch :disabled="loading">{{ $t('showResultsForProductGroups') }}</b-form-checkbox>
                        <b-form-checkbox v-model="showAvailabilityTime" switch :disabled="loading">{{ $t('showAvailabilityTime') }}</b-form-checkbox>
                    </div>
                </div>
            </template>

            <div class="dropdown-divider border-bottom border-light"></div>

            <div class="d-flex gap-4 flex-column">

                <CurrentProductsAvailability />

                <div class="dropdown-divider border-bottom border-light"></div>

                <CurrentUnavailableProducts />

            </div>

        </Card>

    </div>
</template>



<script>
    import FormCategoryTreeSelect from '@/components/common/FormCategoryTreeSelect.vue';
    import Multiselect from '@/components/common/Multiselect.vue';
    import MultiselectAvailableCampaigns from '@/components/common/MultiselectAvailableCampaigns.vue';
    import { assignIfChanged } from '@/utils/util';
    import CurrentProductsAvailability from './CurrentProductsAvailability.vue';
    import CurrentUnavailableProducts from './CurrentUnavailableProducts.vue';

    const DEFAULT_FILTERS_MAP = new Map();

    const DEFAULT_FILTERS = {
        showProductGroupsResults: false,
        showAvailabilityTime: false,
        campaigns: [],
        availabilities: [],
        groups: [],
        categories: [],
    };

    export default {
        name: 'CurrentAvailability',
        components: {
            Multiselect,
            FormCategoryTreeSelect,
            CurrentProductsAvailability,
            CurrentUnavailableProducts,
            MultiselectAvailableCampaigns,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        provide() {
            return {
                parentComponent: this,
            };
        },
        data() {
            return {
                requestCurrentProductsAvailability: this.$req('space/CurrentProductsAvailability'),
                requestCurrentUnavailableProducts: this.$req('space/CurrentUnavailableProducts'),
                requestSpaceGroups: this.$req('producerProductGroups/SpaceGroups'),
                requestOfferAvailabilities: this.$req('offers/OfferAvailabilities'),
                showProductGroupsResults: structuredClone(DEFAULT_FILTERS.showProductGroupsResults),
                showAvailabilityTime: structuredClone(DEFAULT_FILTERS.showAvailabilityTime),
                campaigns: structuredClone(DEFAULT_FILTERS.campaigns),
                availabilities: structuredClone(DEFAULT_FILTERS.availabilities),
                groups: structuredClone(DEFAULT_FILTERS.groups),
                categories: structuredClone(DEFAULT_FILTERS.categories),
                offerAvailabilitiesLoading: false,
                campaignsLoading: false,
                spaceGroupsLoading: false,
                categoriesLoading: false,
                availabilitiesMap: {},
                waitingLoading: false,
            };
        },
        computed: {
            spaceId() {
                return this.$store.state.space.currentSpaceId;
            },
            loadingFilters() {
                return this.campaignsLoading || this.spaceGroupsLoading || this.categoriesLoading;
            },
            loadingData() {
                return this.requestCurrentProductsAvailability.getLoading() || this.requestCurrentUnavailableProducts.getLoading();
            },
            loading() {
                return this.loadingData || this.loadingFilters || this.waitingLoading;
            },
            apiParams() {
                const urlParams = { spaceId: this.spaceId };
                const requestParams = {};
                if (this.campaigns?.length) requestParams.campaigns = [...this.campaigns].sort().join(',');
                if (!this.showProductGroupsResults && this.categories?.length) requestParams.categories = [...this.categories].sort().join(',');
                if (this.showProductGroupsResults && this.groups?.length) requestParams.groups = [...this.groups].sort().join(',');
                return { urlParams, requestParams };
            },
        },
        watch: {
            loadingFilters(newVal, oldVal) {
                if (newVal === false && oldVal === true) {
                    Object.entries(DEFAULT_FILTERS).forEach(([key, val]) => {
                        this.setFilterValue(key, this[key]);
                    });
                }
            },
            spaceId() {
                this.initDefaultFilter();
                this.resetFilters();
            },
            apiParams: {
                immediate: true,
                handler(newVal, oldVal) {
                    if (JSON.stringify(newVal || {}) !== JSON.stringify(oldVal || {})) {
                        // we delay loadData and check if apiParams no longer changes
                        this.waitingLoading = true;
                        setTimeout(() => {
                            if (JSON.stringify(newVal || {}) === JSON.stringify(this.apiParams || {})) {
                                this.loadData();
                            }
                            this.waitingLoading = false;
                        }, 100);
                    }
                },
            },
        },
        methods: {
            initDefaultFilter() {
                if (!DEFAULT_FILTERS_MAP.has(this.spaceId)) {
                    DEFAULT_FILTERS_MAP.set(this.spaceId, structuredClone(DEFAULT_FILTERS));
                }
            },
            setFilterValue(filterName, filterValue) {
                const filters = DEFAULT_FILTERS_MAP.get(this.spaceId) || {};
                filters[filterName] = filterValue;
                DEFAULT_FILTERS_MAP.set(this.spaceId, filters);
            },
            getFilterValue(filterName) {
                const filters = DEFAULT_FILTERS_MAP.get(this.spaceId);
                return filters ? filters[filterName] : structuredClone(DEFAULT_FILTERS[filterName]);
            },
            loadData() {
                if (!this.loadingFilters) {
                    this.abortRequest();
                    this.fetchData();
                }
            },
            abortRequest() {
                this.requestCurrentProductsAvailability.abortAll();
                this.requestCurrentUnavailableProducts.abortAll();
            },
            resetData() {
                this.requestCurrentProductsAvailability.resetAll();
                this.requestCurrentUnavailableProducts.resetAll();
            },
            fetchData() {
                this.requestCurrentProductsAvailability.get(this.apiParams);
                this.requestCurrentUnavailableProducts.get(this.apiParams);
            },
            fetchOfferAvailabilities() {
                return this.requestOfferAvailabilities.get().then(data => {
                    this.availabilitiesMap = data.reduce((acc, item) => ({ ...acc, [item.value]: item.label }), {});
                    return data;
                });
            },
            fetchSpaceGroups() {
                return this.requestSpaceGroups.get({ urlParams: { spaceId: this.spaceId } }).then(data => data.items);
            },
            resetFilters() {
                Object.keys(DEFAULT_FILTERS).forEach(key => {
                    this[key] = assignIfChanged(this[key], structuredClone(this.getFilterValue(key)));
                });
                this.offerAvailabilitiesLoading = false;
                this.campaignsLoading = false;
                this.spaceGroupsLoading = false;
                this.categoriesLoading = false;
            },
        },
        created() {
            this.initDefaultFilter();
            this.parentComponent.$on('resetFilters', this.resetFilters);
        },
        beforeDestroy() {
            this.parentComponent.$off('resetFilters', this.resetFilters);
        },
    };
</script>



<i18n lang="json" locale="pl" scope="local">
{
    "tooltip": "Zestawienie prezentuje przegląd aktualnej dostępności w sklepach oraz dodatkowo listę niedostępnych produktów."
}
</i18n>

<i18n lang="json" locale="en" scope="local">
{
    "tooltip": "The list presents a review of current availability in stores and an additional list of inaccessible products."
}
</i18n>
